import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/base/tooltip";
import { MATERIAL_FORMULA_VARS } from "@/consts/materialFormulaVars";
import { cn } from "@/lib/utils";
import { QuoteCalculator } from "@/models";
import { CrewRates } from "@/models/crew-rates";
import {
  GroupedLineItem,
  LINE_ITEM_TYPES,
  LineItem,
  LineItemSchema,
  UNIT_OPTIONS,
} from "@/models/line-item";
import { MaterialFormula } from "@/models/material-formula";
import { Warranty } from "@/models/warranty";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Calculator,
  Check,
  ChevronDownIcon,
  ChevronRightIcon,
  GripVertical,
  HelpCircle,
  Maximize2,
  Minimize2,
  MinusIcon,
  PlusCircle,
  Ruler,
  TriangleAlert,
} from "lucide-react";
import { motion } from "motion/react";
import React, { useCallback } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Button } from "../base/button";
import { Card, CardContent } from "../base/card";
import { Checkbox } from "../base/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../base/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../base/form";
import { Input } from "../base/input";
import { Popover, PopoverContent, PopoverTrigger } from "../base/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../base/select";
import { OptionWithGreatGrandChildren } from "../controls";
import { LaborTypeCombobox } from "../controls/labor-type-combobox";
import { ProductStyleCombobox } from "../controls/product-style-combobox";
import { EmptyTextProductsPlaceholder } from "../forms/line-items-group";

// Define the interface for labor type options (add after other imports)
interface LaborTypeOption {
  key: string;
  name: string;
  children?: LaborTypeOption[];
}

// Extract DescriptionField component
const DescriptionField: React.FC<{
  field: {
    value: string | null;
    onChange: (value: string) => void;
  };
  compact?: boolean;
  childType: string;
  childForm: UseFormReturn<LineItem>;
  warranties: Warranty[];
  notifyChanges: (data: LineItem) => void;
}> = ({ field, compact, childType, childForm, warranties, notifyChanges }) => {
  // Define Quill formats to be recognized
  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background", // Include background for highlighting
    "list",
    // "bullet",
  ];

  // Define Quill editor modules with highlighter toolbar
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }], // Added background/highlight color
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
  };

  // Add state for cached description changes
  const [cachedChanges, setCachedChanges] = React.useState<Partial<LineItem>>(
    {}
  );
  const [descriptionEditor, setDescriptionEditor] = React.useState({
    open: false,
    maximized: false,
  });

  // Modified handler to cache changes
  const handleDescriptionChange = (field: keyof LineItem, value: string) => {
    setCachedChanges((prev) => ({ ...prev, [field]: value }));
  };

  const quillRef = React.useRef<ReactQuill>(null);
  // Create a ref for the popover trigger element
  const popoverTriggerRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (descriptionEditor.open && childType !== "Summary") {
      // Small delay to ensure the editor is mounted
      setTimeout(() => {
        quillRef.current?.focus();
      }, 100);
    }
  }, [quillRef, compact, popoverTriggerRef, childType, descriptionEditor.open]);

  const handleMaximizeToggle = () => {
    // Toggle the maximized state
    setDescriptionEditor((prev) => ({
      ...prev,
      maximized: !prev.maximized,
    }));

    // If we're maximizing (not already maximized), scroll to the popover trigger
    if (!descriptionEditor.maximized && popoverTriggerRef.current) {
      // Use scrollIntoView with smooth behavior
      popoverTriggerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  if (childType === "Warranty" && warranties.length > 0) {
    return (
      <Select
        onValueChange={(value) => {
          const warranty = warranties.find((w) => w.wtitle === value);
          if (warranty) {
            switch (warranty.wexptype) {
              case "perccont":
                childForm.setValue("unit", "PERCCONT");
                break;
              case "numsquares":
                childForm.setValue("unit", "SF");
                break;
              default:
                childForm.setValue("unit", "EA");
                break;
            }

            childForm.setValue("description", warranty.wdesc?.toString() ?? "");

            if (warranty.wexptype === "perccont") {
              childForm.setValue(
                "quantity",
                warranty.wexpamt ? Number(warranty.wexpamt) : null
              );
              childForm.setValue("unitCost", null);
            } else {
              childForm.setValue(
                "unitCost",
                warranty.wexpamt ? Number(warranty.wexpamt) : null
              );
              childForm.setValue("quantity", null);
            }
          }
          field.onChange(value);
        }}
        defaultValue={field.value ?? undefined}
      >
        <FormControl>
          <SelectTrigger
            className={cn({
              "h-7 text-xs": compact,
            })}
          >
            <SelectValue placeholder="Choose a Warranty" />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          {warranties
            .filter((warranty) => warranty.wexptype !== "perccont")
            .map((warranty) => (
              <SelectItem key={warranty.wid} value={warranty.wtitle ?? ""}>
                {warranty.wtitle}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className="flex gap-2">
      <Popover
        open={descriptionEditor.open}
        onOpenChange={(open) => {
          if (!open) {
            // Apply cached changes when popover closes
            Object.entries(cachedChanges).forEach(([field, value]) => {
              childForm.setValue(field as keyof LineItem, value);
            });
            setCachedChanges({});
            notifyChanges({ ...childForm.getValues(), ...cachedChanges });
          }
          setDescriptionEditor((prev) => ({ ...prev, open }));
        }}
      >
        <PopoverTrigger asChild>
          <div
            ref={popoverTriggerRef}
            className="overflow-auto w-full cursor-text"
          >
            <div
              className={cn(
                "flex overflow-hidden relative px-2 py-3 w-full h-10 leading-3 whitespace-nowrap rounded border text-ellipsis before:absolute before:bottom-0 before:left-0 before:right-0 before:h-4 before:bg-gradient-to-t before:from-white before:to-transparent",
                {
                  "py-1 h-7 text-xs": compact,
                }
              )}
              dangerouslySetInnerHTML={{ __html: field.value ?? "" }}
            />
            <Input
              {...field}
              value={field.value ?? ""}
              onChange={(e) => field.onChange(e.target.value)}
              className="hidden"
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "transition-all duration-200",
            descriptionEditor.maximized ? "w-[90vw] max-w-[90vw]" : "w-[600px]"
          )}
          style={{
            position: descriptionEditor.maximized ? "relative" : undefined,
            left: descriptionEditor.maximized ? "5vw" : undefined,
            height: descriptionEditor.maximized
              ? "calc(90vh - 120px)"
              : undefined,
          }}
        >
          <div className="space-y-4 h-full">
            {childType === "Summary" && (
              <div className="space-y-2">
                <FormLabel className="mb-0 whitespace-nowrap">Title</FormLabel>
                <Input
                  value={
                    cachedChanges.title ?? childForm.getValues("title") ?? ""
                  }
                  onChange={(e) =>
                    handleDescriptionChange("title", e.target.value)
                  }
                />
              </div>
            )}
            <div
              className="space-y-2 grid grid-cols-1 grid-rows-[auto_1fr] h-full"
              style={{
                maxHeight: descriptionEditor.maximized ? "100%" : "40vh",
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center">
                <FormLabel className="mb-0 whitespace-nowrap">
                  Description
                </FormLabel>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleMaximizeToggle}
                >
                  {descriptionEditor.maximized ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                  <span className="sr-only">
                    {descriptionEditor.maximized ? "Minimize" : "Maximize"}
                  </span>
                </Button>
              </div>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={
                  cachedChanges.description ??
                  childForm.getValues("description") ??
                  ""
                }
                onChange={(content) =>
                  handleDescriptionChange("description", content)
                }
                modules={quillModules}
                formats={quillFormats}
                className="grid grid-cols-1 grid-rows-[auto_1fr] overflow-y-auto [&>.ql-container]:overflow-y-auto"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Move LineItemFields component here
const LineItemFields = React.memo(function LineItemFields({
  value,
  compact = false,
  productStyleOptions,
  warranties,
  materialFormulas,
  quoteCalculator,
  isTemplate,
  notifyChanges,
  enableFormulas,
  laborTypeOptions,
  crewRates,
}: {
  value: LineItem;
  compact?: boolean;
  productStyleOptions: OptionWithGreatGrandChildren[];
  warranties: Warranty[];
  materialFormulas: MaterialFormula[];
  quoteCalculator?: QuoteCalculator | null;
  isTemplate?: boolean;
  notifyChanges: (data: LineItem) => void;
  enableFormulas: boolean;
  laborTypeOptions?: LaborTypeOption[];
  crewRates?: CrewRates | null;
}) {
  // Dynamic labor type options based on dpitch in qcdata
  const dynamicLaborTypeOptions = React.useMemo(() => {
    // If no laborTypeOptions provided or no quoteCalculator, return the passed options
    if (!laborTypeOptions || !quoteCalculator?.qcdata) {
      return laborTypeOptions;
    }

    try {
      const qcdata = JSON.parse(quoteCalculator.qcdata);
      const dpitch = qcdata.dpitch;

      // If dpitch is set, return a filtered/modified set of labor type options
      if (dpitch !== undefined && dpitch !== null && dpitch !== "") {
        return [
          {
            key: "extras",
            name: "Extras",
            children: [
              {
                key: "chimney_flashings_large_1",
                name: "Chimney Flashings Large",
              },
              {
                key: "chimney_flashings_small_1",
                name: "Chimney Flashings Small",
              },
              {
                key: "cut_in_ridge_vent_1",
                name: "Cut In Ridge Vent",
              },
              {
                key: "cut_in_vents_1",
                name: "Cut In Vents",
              },
              {
                key: "drip_edge_1",
                name: "Drip Edge",
              },
              {
                key: "gutter_apron_1",
                name: "Gutter Apron",
              },
              {
                key: "install_ridge_vent_1",
                name: "Install Ridge Vent",
              },
              {
                key: "plug_vents_1",
                name: "Plug Vents",
              },
              {
                key: "power_vents_1",
                name: "Power Vents",
              },
              {
                key: "skylights_large_install_1",
                name: "Skylights Large Install",
              },
              {
                key: "skylights_small_install_1",
                name: "Skylights Small Install",
              },
              {
                key: "step_flashing_1",
                name: "Step Flashing",
              },
              {
                key: "straight_flashing_1",
                name: "Straight Flashing",
              },
              {
                key: "z_flashing_large_1",
                name: "Z Flashing Large",
              },
              {
                key: "z_flashing_small_1",
                name: "Z Flashing Small",
              },
            ],
          },
          {
            key: "install_rates",
            name: "Install Rates",
            children: [
              {
                key: `${dpitch.replace("/", "_")}_1`,
                name: `Dominant Pitch (${dpitch})`,
              },
              {
                key: "height_upcharge_1",
                name: "Height Upcharge",
              },
              {
                key: "ridge_1",
                name: "Ridge",
              },
              {
                key: "starter_1",
                name: "Starter",
              },
              {
                key: "walk_on_1",
                name: "Walk On",
              },
            ],
          },
          {
            key: "tearoff",
            name: "Tearoff",
            children: [
              {
                key: "additional_layer_upcharge_1",
                name: "Additional Layer Upcharge",
              },
              {
                key: "plywood_replacement_1",
                name: "Plywood Replacement",
              },
              {
                key: "tearoff_1",
                name: "Tearoff",
              },
            ],
          },
          {
            key: "hourly",
            name: "Hourly",
            children: [
              {
                key: "hourly_1",
                name: "Hourly Installer",
              },
            ],
          },
        ];
      }

      // If dpitch is not set, return the original laborTypeOptions
      return laborTypeOptions;
    } catch (error) {
      console.error("Error parsing qcdata for labor type options:", error);
      // Fallback to original options if parsing fails
      return laborTypeOptions;
    }
  }, [laborTypeOptions, quoteCalculator?.qcdata]);

  const laborTypeUnitMappings = {
    chimney_flashings_large_1: "EA",
    chimney_flashings_small_1: "EA",
    cut_in_ridge_vent_1: "EA",
    cut_in_vents_1: "LF",
    drip_edge_1: "LF",
    gutter_apron_1: "LF",
    install_ridge_vent_1: "EA",
    plug_vents_1: "EA",
    power_vents_1: "EA",
    skylights_large_install_1: "EA",
    skylights_small_install_1: "EA",
    step_flashing_1: "LF",
    straight_flashing_1: "LF",
    z_flashing_large_1: "EA",
    z_flashing_small_1: "EA",
    "4_12_1": "SQ",
    "5_12_1": "SQ",
    "6_12_1": "SQ",
    "7_12_1": "SQ",
    "8_12_1": "SQ",
    "9_12_1": "SQ",
    "10_12_1": "SQ",
    "11_12_1": "SQ",
    "12_12_1": "SQ",
    height_upcharge_1: "EA",
    ridge_1: "LF",
    starter_1: "LF",
    walk_on_1: "SQ",
    additional_layer_upcharge_1: "SQ",
    plywood_replacement_1: "EA",
    tearoff_1: "SQ",
    mansard_1: "SQ",
    hourly_1: "HR",
  } as const;

  const childForm = useForm<LineItem>({
    resolver: zodResolver(LineItemSchema),
    defaultValues: value,
  });
  const childType = childForm.watch("type");

  const [materialTypeLabel, setMaterialTypeLabel] = React.useState("");
  const [materialStyleLabel, setMaterialStyleLabel] = React.useState("");
  const [materialSupplierLabel, setMaterialSupplierLabel] = React.useState("");
  const [materialCoverage, setMaterialCoverage] = React.useState<number | null>(
    null
  );
  const [filteredFormulas, setFilteredFormulas] = React.useState<
    MaterialFormula[]
  >([]);
  const [missingFormulaVars, setMissingFormulaVars] =
    React.useState<boolean>(false);
  const lastProcessedFormulaRef = React.useRef<number | null>(null);

  const [laborCategoryLabel, setLaborCategoryLabel] = React.useState("");
  const [laborTypeLabel, setLaborTypeLabel] = React.useState("");

  // Function to extract variables from formula string
  const extractVariables = (formula: string): string[] => {
    const matches = formula.match(/\{([^}]+)\}/g);
    return matches ? matches.map((match) => match.slice(1, -1)) : [];
  };

  // Function to calculate unit cost based on crew rates
  const calculateUnitCostFromCrewRates = React.useCallback(
    (laborTypeId: string) => {
      if (!crewRates || !dynamicLaborTypeOptions) return null;

      // Find the matching crew rate - need to determine the category first
      let crewRateKey: keyof CrewRates | null = null;

      // Look through the labor type options to find which category this value belongs to
      for (const category of dynamicLaborTypeOptions) {
        const foundChild = category.children?.find(
          (child) => child.key === laborTypeId
        );
        if (foundChild) {
          // Construct the crew rate key: {category_key}_{labor_type_key}
          crewRateKey = `${category.key}_${laborTypeId}` as keyof CrewRates;
          break;
        }
      }

      if (crewRateKey && crewRates[crewRateKey]) {
        const unitCost = parseFloat(crewRates[crewRateKey]);
        if (!isNaN(unitCost)) {
          return unitCost;
        }
      }

      return null;
    },
    [crewRates, dynamicLaborTypeOptions]
  );

  // Effect to update unit cost when crew rates or labor type changes
  React.useEffect(() => {
    const currentLaborTypeId = childForm.getValues("laborTypeId");

    if (currentLaborTypeId && childType === "Labor") {
      const unitCost = calculateUnitCostFromCrewRates(currentLaborTypeId);
      if (unitCost !== null) {
        const currentUnitCost = childForm.getValues("unitCost");
        if (currentUnitCost !== unitCost) {
          childForm.setValue("unitCost", unitCost);
          notifyChanges(childForm.getValues());
        }
      }
    }
  }, [crewRates]);

  React.useEffect(() => {
    console.log("appliedFormulaId changed", childForm.watch("appliedFormulaId"));
  }, [childForm.watch("appliedFormulaId")]);

  // Function to filter material formulas
  const filterMaterialFormulas = React.useCallback(() => {
    if (!materialFormulas || (!quoteCalculator && !isTemplate)) return [];

    const currentType = childForm.getValues("type");
    if (currentType !== "Product" && currentType !== "Service") return [];

    const currentMaterialTypeId = childForm.getValues("materialTypeId");
    if (!currentMaterialTypeId) return [];

    const filteredFormulas = materialFormulas.filter((formula) => {
      // Check if formula's mfmid matches current materialTypeId
      if (formula.mfmid !== currentMaterialTypeId) return false;
      return true;
    });

    return filteredFormulas;
  }, [childForm, materialFormulas, quoteCalculator, isTemplate]);

  // Update filtered formulas when relevant dependencies change
  React.useEffect(() => {
    const filtered = filterMaterialFormulas();
    setFilteredFormulas(filtered);
  }, [filterMaterialFormulas]);

  // Initialize materialCoverage from existing data when component mounts
  React.useEffect(() => {
    const materialStyleId = value.materialStyleId;
    const materialTypeId = value.materialTypeId;
    
    if (materialStyleId && materialTypeId && productStyleOptions.length > 0) {
      // Find the coverage value from productStyleOptions
      const typeOption = productStyleOptions.find(opt => opt.value === materialTypeId.toString());
      if (typeOption) {
        // Search through all brands to find the style
        for (const brand of typeOption.children) {
          const styleOption = brand.children.find(style => style.value === materialStyleId.toString());
          if (styleOption && styleOption.coverage !== undefined && styleOption.coverage !== null) {
            setMaterialCoverage(styleOption.coverage);
            break;
          }
        }
      }
    }
  }, [value.materialStyleId, value.materialTypeId, productStyleOptions]);

  // Initialize material labels from existing data when component mounts
  React.useEffect(() => {
    const materialStyleId = value.materialStyleId;
    const materialTypeId = value.materialTypeId;
    const materialSupplierId = value.materialSupplierId;
    
    if (productStyleOptions.length > 0) {
      // Find and set type label
      if (materialTypeId) {
        const typeOption = productStyleOptions.find(opt => opt.value === materialTypeId.toString());
        if (typeOption) {
          setMaterialTypeLabel(typeOption.label);
          
          // Find and set style and supplier labels
          if (materialStyleId) {
            for (const brand of typeOption.children) {
              const styleOption = brand.children.find(style => style.value === materialStyleId.toString());
              if (styleOption) {
                setMaterialStyleLabel(styleOption.label);
                
                // Find and set supplier label
                if (materialSupplierId) {
                  const supplierOption = styleOption.children.find(supplier => supplier.value === materialSupplierId.toString());
                  if (supplierOption) {
                    setMaterialSupplierLabel(supplierOption.label);
                  }
                }
                break;
              }
            }
          }
        }
      }
    }
  }, [value.materialStyleId, value.materialTypeId, value.materialSupplierId, productStyleOptions]);

  // Watch for changes that should trigger formula updates
  const watchedMaterialStyleId = childForm.watch("materialStyleId");
  const watchedType = childForm.watch("type");
  const watchedFormulaId = childForm.watch("appliedFormulaId");

  // Filter material formulas when relevant values change
  React.useEffect(() => {
    const filtered = filterMaterialFormulas();
    setFilteredFormulas(filtered);

    // If there are no formulas for the current material childType, clear any selected formula
    if (
      !watchedMaterialStyleId &&
      filtered.length === 0 &&
      childForm.getValues("appliedFormulaId")
    ) {
      childForm.setValue("appliedFormulaId", null);
      lastProcessedFormulaRef.current = null;
    }
  }, [
    filterMaterialFormulas,
    watchedMaterialStyleId,
    watchedType,
    materialCoverage,
    childForm,
  ]);

  // Handle formula calculations
  React.useEffect(() => {
    // Skip if no formula or already processed this formula
    if (
      !watchedFormulaId ||
      !quoteCalculator ||
      lastProcessedFormulaRef.current === watchedFormulaId ||
      !materialCoverage ||
      !enableFormulas
    ) {
      return;
    }

    const formula = filteredFormulas.find((f) => f.mfid === watchedFormulaId);
    if (!formula || !formula.mfformula) return;

    // Parse formula - split by " = " to get quantity calculation and unit
    const [leftSide, rightSide] = formula.mfformula.split(" = ");

    if (!leftSide || !rightSide) return;

    // Prepare to substitute variables
    let quantityFormula = leftSide;

    // Get qcdata from quote calculator
    let qcdata: Record<string, string | number | boolean | null> = {};
    try {
      qcdata = JSON.parse(quoteCalculator.qcdata || "{}");
    } catch (e) {
      console.error("Error parsing qcdata:", e);
      return;
    }

    // Replace all variables in curly braces
    const variables = extractVariables(leftSide);

    for (const variable of variables) {
      if (variable === "coverage") {
        // Use coverageFactor for coverage variable
        quantityFormula = quantityFormula.replace(
          `{${variable}}`,
          materialCoverage.toString()
        );
      } else if (
        qcdata[variable] !== undefined &&
        qcdata[variable] !== null &&
        qcdata[variable] !== ""
      ) {
        if (variable === "wf") {
          quantityFormula = quantityFormula.replace(
            `{${variable}}`,
            (Number(qcdata[variable]) / 100).toString()
          );
        } else {
          // Use qcdata for other variables
          quantityFormula = quantityFormula.replace(
            `{${variable}}`,
            qcdata[variable].toString()
          );
        }
      } else {
        // If any required variable is missing, abort
        setMissingFormulaVars(true);
        return;
      }
    }

    setMissingFormulaVars(false);

    try {
      // Calculate quantity using evaluated formula
      const calculatedQuantity = [eval][0](quantityFormula);
      const formattedQuantity = Math.ceil(
        parseFloat(calculatedQuantity.toFixed(2))
      );
      const unitValue = rightSide.trim();

      // Remember this formula as processed
      lastProcessedFormulaRef.current = watchedFormulaId;

      // Only trigger onChange if values have changed
      const currentQuantity = childForm.getValues("quantity");
      const currentUnit = childForm.getValues("unit");

      // Set the values in the childForm
      childForm.setValue("quantity", formattedQuantity);
      childForm.setValue("unit", unitValue);

      if (currentQuantity !== formattedQuantity || currentUnit !== unitValue) {
        // Trigger onChange only for actual changes
        notifyChanges({
          ...childForm.getValues(),
          quantity: formattedQuantity,
          unit: unitValue,
        });
      }
    } catch (error) {
      console.error("Error calculating formula:", error);
    }
  }, [watchedFormulaId, filteredFormulas, materialCoverage, childForm]);

  // Handle resetting lastProcessedFormulaRef when formula is removed
  React.useEffect(() => {
    if (!watchedFormulaId) {
      lastProcessedFormulaRef.current = null;
    }
  }, [watchedFormulaId]);

  React.useEffect(() => {
    const subscription = childForm.watch((data) => {
      if (data.type === "Separator") {
        data.description = null;
      } else if (data.type === "Product" || data.type === "Service") {
        data.materialStyle = (
          (materialSupplierLabel ?? "") +
          " " +
          (materialStyleLabel ?? "") +
          " " +
          (materialTypeLabel ?? "")
        ).trim();
      } else if (data.type === "Labor") {
        // Set description for labor items
        data.description = laborTypeLabel || null;
      }
      notifyChanges(data as LineItem);
    });
    return () => subscription.unsubscribe();
  }, [
    childForm,
    materialTypeLabel,
    materialStyleLabel,
    materialSupplierLabel,
    laborCategoryLabel,
    laborTypeLabel,
    notifyChanges,
  ]);

  // Handle clearing formula when related properties change
  React.useEffect(() => {
    // Clear appliedFormulaId when childType or materialTypeId changes
    if (childForm.getValues("appliedFormulaId") && !watchedMaterialStyleId) {
      childForm.setValue("appliedFormulaId", null);
      lastProcessedFormulaRef.current = null;
    }
  }, [watchedMaterialStyleId, watchedType, childForm]);

  // Add useEffect to set markup to zero for Text items with children
  React.useEffect(() => {
    const hasChildren =
      "children" in value &&
      Array.isArray(value.children) &&
      value.children.length > 0;
    if (childType === "Text" && hasChildren) {
      const currentMarkup = childForm.getValues("markup");
      if (currentMarkup !== 0) {
        childForm.setValue("markup", 0);
        notifyChanges({ ...childForm.getValues(), markup: 0 });
      }
    }
  }, [childType, value, childForm, notifyChanges]);

  // Add helper to check if product is inside text
  const isNestedProduct = !!value.parentTextTrackingId;
  const isText = childType === "Text";

  // Check if Text item has children for markup field visibility
  const hasChildren =
    "children" in value &&
    Array.isArray(value.children) &&
    value.children.length > 0;

  // Calculate sum of children prices for text items
  const childrenTotalPrice = React.useMemo(() => {
    if (childType !== "Text" || !hasChildren) return 0;

    return ((value as GroupedLineItem).children || []).reduce((sum, child) => {
      const quantity = child.quantity ?? 0;
      const unitCost = child.unitCost ?? 0;
      const markup = child.markup ?? 0;
      const price = quantity * unitCost * (1 + markup / 100);
      return sum + price;
    }, 0);
  }, [childType, hasChildren, value]);

  // Add useEffect to set applyTax to false for Text items with children
  React.useEffect(() => {
    const hasChildren =
      "children" in value &&
      Array.isArray(value.children) &&
      value.children.length > 0;
    if (childType === "Text" && hasChildren) {
      const currentApplyTax = childForm.getValues("applyTax");
      if (currentApplyTax !== false) {
        childForm.setValue("applyTax", false);
        notifyChanges({ ...childForm.getValues(), applyTax: false });
      }
    }
  }, [childType, value, childForm, notifyChanges]);

  // Add useEffect to update form values for Text items with children
  React.useEffect(() => {
    const hasChildren =
      "children" in value &&
      Array.isArray(value.children) &&
      value.children.length > 0;
    if (childType === "Text" && hasChildren && childrenTotalPrice > 0) {
      const currentUnitCost = childForm.getValues("unitCost");
      const currentQuantity = childForm.getValues("quantity");

      // Set unitCost to the total price of children and quantity to 1
      if (currentUnitCost !== childrenTotalPrice || currentQuantity !== 1) {
        childForm.setValue("unitCost", childrenTotalPrice);
        childForm.setValue("quantity", 1);
        notifyChanges({
          ...childForm.getValues(),
          unitCost: childrenTotalPrice,
          quantity: 1,
        });
      }
    }
  }, [childType, value, childForm, notifyChanges, childrenTotalPrice]);

  // Add formatFormula here
  const formatFormula = (formula: string, injectValues?: boolean) => {
    if (!formula) return "";

    // Split the formula into left side and right side (separated by " = ")
    const [calculation, unit] = formula.split(" = ");

    if (!calculation || !unit) return formula;

    // Replace variable names with their English names
    let formattedCalculation = calculation;

    // Extract variables from the calculation
    const variables = extractVariables(calculation);

    // Process each variable
    for (const variable of variables) {
      // Check if the variable is in MATERIAL_FORMULA_VARS
      if (variable === "coverage") {
        if (!injectValues) {
          formattedCalculation = formattedCalculation.replace(
            `{${variable}}`,
            `{Coverage}`
          );
        } else if (
          materialCoverage !== null &&
          materialCoverage !== undefined
        ) {
          formattedCalculation = formattedCalculation.replace(
            `{${variable}}`,
            materialCoverage.toString()
          );
        }
      } else if (
        !injectValues &&
        MATERIAL_FORMULA_VARS[variable as keyof typeof MATERIAL_FORMULA_VARS]
      ) {
        const englishName =
          MATERIAL_FORMULA_VARS[variable as keyof typeof MATERIAL_FORMULA_VARS];
        formattedCalculation = formattedCalculation.replace(
          `{${variable}}`,
          `{${englishName}}`
        );
      } else if (injectValues) {
        try {
          // If injectValues is true, replace with the actual value
          const qcdata = JSON.parse(quoteCalculator?.qcdata || "{}");
          const value =
            variable === "wf" ? qcdata[variable] / 100 : qcdata[variable];

          if (value !== undefined && value !== null) {
            formattedCalculation = formattedCalculation.replace(
              `{${variable}}`,
              value.toString()
            );
          }
        } catch (error) {
          console.error("Error parsing qcdata:", error);
        }
      }
      // If not found, leave it as is
    }

    // Format the full formula with the processed calculation and unit
    return `${formattedCalculation} = ${
      injectValues ? childForm.watch("quantity") : unit
    }`;
  };

  return (
    <div
      className={cn("@xl:flex-1 @xl:overflow-x-hidden", {
        "text-xs": compact,
      })}
    >
      <div
        className={cn(
          "flex flex-col space-y-4 @xl:flex-row @xl:space-y-0 @xl:space-x-2 @xl:grow @xl:pr-2 @xl:w-full",
          {
            "space-y-2": compact,
          }
        )}
      >
        <div
          className={cn(
            "flex flex-col space-y-4 @xl:space-y-0 @xl:space-x-2 @xl:flex-row @xl:items-start @xl:w-1/2 @xl:grow-0 @xl:shrink-0",
            {
              "space-y-2": compact,
            }
          )}
        >
          {(childType === "Product" || childType === "Service") && (
            <div
              className={cn("flex flex-col w-full space-y-2 @xl:w-2/5", {
                "space-y-1": compact,
                "@xl:w-full": isNestedProduct && childType === "Product",
              })}
            >
              <div
                className={cn("flex gap-2 items-center", {
                  "gap-1": compact,
                })}
              >
                <FormLabel
                  className={cn("mb-0 whitespace-nowrap", {
                    "text-xs": compact,
                  })}
                >
                  Product Style
                </FormLabel>
                {enableFormulas &&
                childForm.watch("materialStyleId") &&
                ((materialCoverage !== null &&
                  materialCoverage !== undefined &&
                  materialCoverage > 0) ||
                  isTemplate) &&
                filteredFormulas.length > 0 ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                className={cn("w-4 h-4", {
                                  "w-3 h-3": compact,
                                })}
                              >
                                <Ruler
                                  className={cn("w-4 h-4", {
                                    "text-muted-foreground opacity-70":
                                      !childForm.watch("appliedFormulaId"),
                                    "text-green-700":
                                      childForm.watch("appliedFormulaId"),
                                    "w-3 h-3": compact,
                                  })}
                                />
                                <span className="sr-only">View Formulas</span>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 w-96">
                              <Command>
                                <CommandInput placeholder="Search formulas..." />
                                <CommandList>
                                  <CommandEmpty>
                                    No formulas found.
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {filteredFormulas.map((formula) => (
                                      <CommandItem
                                        key={formula.mfid}
                                        value={`${formula.mfid}`}
                                        onSelect={(value) => {
                                          const currentFormulaId =
                                            childForm.getValues(
                                              "appliedFormulaId"
                                            );
                                          // Toggle the selection
                                          childForm.setValue(
                                            "appliedFormulaId",
                                            currentFormulaId === +value
                                              ? null
                                              : +value
                                          );
                                          // Manually trigger onChange with current childForm values
                                          const popover =
                                            document.querySelector(
                                              '[role="dialog"]'
                                            );
                                          if (popover) {
                                            const trigger =
                                              document.querySelector(
                                                `[aria-controls="${popover.id}"]`
                                              );
                                            trigger?.dispatchEvent(
                                              new Event("click", {
                                                bubbles: true,
                                              })
                                            );
                                          }
                                        }}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            childForm.watch(
                                              "appliedFormulaId"
                                            ) === formula.mfid
                                              ? "opacity-100"
                                              : "opacity-0",
                                            { "h-3 w-3 mr-1": compact }
                                          )}
                                        />
                                        <div className="flex justify-between items-center w-full">
                                          <span>{formula.mfname}</span>
                                          <span className="text-sm text-muted-foreground">
                                            {formatFormula(formula.mfformula)}
                                          </span>
                                        </div>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </span>
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent>
                          <p>
                            {childForm.watch("appliedFormulaId")
                              ? `Formula selected: ${
                                  materialFormulas.find(
                                    (f) =>
                                      f.mfid ===
                                      childForm.watch("appliedFormulaId")
                                  )?.mfname ?? ""
                                }`
                              : "Select a formula"}
                          </p>
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
              </div>
              <ProductStyleCombobox
                options={productStyleOptions}
                defaultTypeValue={
                  value.materialTypeId ? `${value.materialTypeId}` : ""
                }
                defaultStyleValue={
                  value.materialStyleId ? `${value.materialStyleId}` : ""
                }
                defaultSupplierValue={
                  value.materialSupplierId ? `${value.materialSupplierId}` : ""
                }
                compact={compact}
                onTypeChange={(value, label) => {
                  const currentValue = childForm.getValues("materialTypeId");
                  if (currentValue !== Number(value)) {
                    childForm.setValue("materialTypeId", Number(value));
                    setMaterialTypeLabel(label);
                  }
                }}
                onStyleChange={(value, label, coverage) => {
                  setMaterialStyleLabel(label);
                  setMaterialCoverage(coverage ?? null);
                  const currentValue = childForm.getValues("materialStyleId");
                  if (currentValue !== Number(value)) {
                    childForm.setValue("materialStyleId", Number(value));
                  }
                }}
                onSupplierChange={(value, label, price, unit) => {
                  const currentValue =
                    childForm.getValues("materialSupplierId");
                  if (currentValue !== Number(value)) {
                    childForm.setValue("materialSupplierId", Number(value));
                    childForm.setValue(
                      "unitCost",
                      price ? Number(price) : null
                    );
                    childForm.setValue("unit", unit ?? null);
                    setMaterialSupplierLabel(label);
                  }
                }}
              />
            </div>
          )}
          {childType === "Labor" && dynamicLaborTypeOptions && (
            <div
              className={cn("flex flex-col w-full space-y-2 @xl:w-2/5", {
                "space-y-1": compact,
                "@xl:w-full": compact,
              })}
            >
              <FormLabel
                className={cn("mb-0 whitespace-nowrap", {
                  "text-xs": compact,
                })}
              >
                Labor Type
              </FormLabel>
              <LaborTypeCombobox
                options={dynamicLaborTypeOptions}
                defaultCategoryValue={value.laborCategoryId ?? ""}
                defaultTypeValue={value.laborTypeId ?? ""}
                compact={compact}
                onCategoryChange={(value, label) => {
                  setLaborCategoryLabel(label);
                  childForm.setValue("laborCategoryId", value);
                }}
                onTypeChange={(value, label) => {
                  setLaborTypeLabel(label);
                  childForm.setValue("laborTypeId", value);

                  // Set the unit based on the labor type mapping
                  const unit =
                    laborTypeUnitMappings[
                      value as keyof typeof laborTypeUnitMappings
                    ];
                  if (unit) {
                    childForm.setValue("unit", unit);
                  }

                  // Set the unit cost based on crew rates
                  const unitCost = calculateUnitCostFromCrewRates(value);
                  if (unitCost !== null) {
                    childForm.setValue("unitCost", unitCost);
                  }

                  childForm.setValue("quantity", 1);
                  if (quoteCalculator?.qcdata) {
                    try {
                      const qcdata = JSON.parse(quoteCalculator.qcdata);
                      const sqFt = Number(qcdata.sqf);
                      if (sqFt && unit === "SQ") {
                        childForm.setValue("quantity", sqFt);
                      }
                    } catch (error) {
                      console.error("Error parsing qcdata:", error);
                    }
                  }

                  notifyChanges(childForm.getValues());
                }}
              />
            </div>
          )}
          <FormField
            control={childForm.control}
            name={
              childType === "Summary" || childType === "Warranty"
                ? "title"
                : "description"
            }
            render={({ field }) => (
              <FormItem
                className={cn(
                  `flex flex-col w-full space-y-2 @xl:grow-0 overflow-auto`,
                  (childType === "Product" ||
                    childType === "Service" ||
                    childType === "Labor") &&
                    !isNestedProduct
                    ? "@xl:w-3/5"
                    : "@xl:w-full",
                  {
                    "space-y-1": compact,
                    hidden: isNestedProduct || childType === "Labor",
                  }
                )}
              >
                <FormLabel
                  className={cn("mb-0 whitespace-nowrap", {
                    "text-xs": compact,
                  })}
                >
                  {childType === "Summary" || childType === "Warranty"
                    ? "Title"
                    : "Description"}
                </FormLabel>
                <FormControl>
                  <DescriptionField
                    field={field}
                    compact={compact}
                    childType={childType}
                    childForm={childForm}
                    warranties={warranties}
                    notifyChanges={notifyChanges}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div
          className={cn(
            "flex flex-col space-y-4 @md:space-y-0 @md:space-x-2 @md:flex-row @md:items-start @xl:w-1/2 @xl:grow-0 @xl:shrink-0",
            {
              "space-y-2": compact,
            }
          )}
        >
          <div
            className={cn(
              `grid ${
                isTemplate ? "grid-cols-6" : "grid-cols-4 @md:grid-cols-8"
              } items-start gap-2 @md:flex @md:w-full`,
              {
                "gap-1": compact,
              }
            )}
          >
            {!(isText && hasChildren) && (
              <FormField
                control={childForm.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem
                    className={cn("flex flex-col space-y-2 @md:w-full", {
                      "space-y-1": compact,
                    })}
                  >
                    <div
                      className={cn("flex gap-2 items-center", {
                        "gap-1": compact,
                      })}
                    >
                      <FormLabel
                        className={cn("mb-0 whitespace-nowrap", {
                          "text-xs": compact,
                        })}
                      >
                        Quantity
                      </FormLabel>
                      {enableFormulas &&
                        !isTemplate &&
                        childForm.watch("appliedFormulaId") &&
                        !missingFormulaVars && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Calculator
                                    className={cn("w-4 h-4 text-green-700", {
                                      "w-3 h-3": compact,
                                    })}
                                  />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {formatFormula(
                                    materialFormulas.find(
                                      (f) =>
                                        f.mfid ===
                                        childForm.watch("appliedFormulaId")
                                    )?.mfformula ?? "",
                                    true
                                  )}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      {enableFormulas &&
                        !isTemplate &&
                        childForm.watch("appliedFormulaId") &&
                        missingFormulaVars && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <TriangleAlert
                                    className={cn("w-4 h-4 text-yellow-500", {
                                      "w-3 h-3": compact,
                                    })}
                                  />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  Formula is missing values. Please enter the
                                  required values on the "Measurements" page.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                    </div>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => {
                          if (!isTemplate) {
                            childForm.setValue("appliedFormulaId", null);
                          }
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null
                          );
                        }}
                        value={field.value ?? ""}
                        readOnly={
                          childType === "Separator" || childType === "Signature"
                        }
                        className={cn({
                          "px-2 py-1 h-7 text-xs": compact,
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {!(isText && hasChildren) && (
              <FormField
                control={childForm.control}
                name="unit"
                render={({ field }) => (
                  <FormItem
                    className={cn("flex flex-col space-y-2 @md:w-full", {
                      "space-y-1": compact,
                    })}
                  >
                    <div
                      className={cn("flex relative gap-2 items-center", {
                        "gap-1": compact,
                      })}
                    >
                      <FormLabel
                        className={cn("mb-0 whitespace-nowrap", {
                          "text-xs": compact,
                        })}
                      >
                        Unit
                      </FormLabel>
                    </div>
                    <Select
                      onValueChange={(e) => {
                        if (!isTemplate) {
                          childForm.setValue("appliedFormulaId", null);
                        }
                        field.onChange(e);
                      }}
                      defaultValue={field.value ?? undefined}
                      value={field.value ?? ""}
                      disabled={
                        childType === "Separator" ||
                        childType === "Signature" ||
                        childType === "Warranty"
                      }
                    >
                      <FormControl>
                        <SelectTrigger
                          className={cn({
                            "h-7 text-xs": compact,
                          })}
                        >
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {UNIT_OPTIONS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                        {childForm.watch("type") === "Warranty" && (
                          <SelectItem key="PERCCONT" value="PERCCONT">
                            % of Contract
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {childForm.watch("unit") !== "PERCCONT" && !(isText && hasChildren) && (
              <FormField
                control={childForm.control}
                name="unitCost"
                render={({ field }) => (
                  <FormItem
                    className={cn("flex flex-col space-y-2 @md:w-full", {
                      "space-y-1": compact,
                    })}
                  >
                    <FormLabel
                      className={cn("mb-0 whitespace-nowrap", {
                        "text-xs": compact,
                      })}
                    >
                      Unit Cost
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                        value={field.value ?? ""}
                        readOnly={
                          childType === "Separator" ||
                          childType === "Signature" ||
                          childType === "Warranty"
                        }
                        className={cn({
                          "px-2 py-1 h-7 text-xs": compact,
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {childForm.watch("unit") !== "PERCCONT" && !isTemplate && (
              <FormItem
                className={cn("flex flex-col space-y-2 @md:w-full", {
                  "space-y-1": compact,
                  hidden: childType === "Text" && hasChildren,
                })}
              >
                <FormLabel
                  className={cn("mb-0 whitespace-nowrap", {
                    "text-xs": compact,
                  })}
                >
                  Cost
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    value={
                      childType === "Text" && hasChildren
                        ? childrenTotalPrice.toFixed(2)
                        : childForm.watch("quantity") != null &&
                          childForm.watch("unitCost") != null
                        ? (
                            (childForm.watch("quantity") ?? 0) *
                            (childForm.watch("unitCost") ?? 0)
                          ).toFixed(2)
                        : "0"
                    }
                    readOnly
                    className={cn({
                      "px-2 py-1 h-7 text-xs": compact,
                    })}
                  />
                </FormControl>
              </FormItem>
            )}

            {childForm.watch("unit") !== "PERCCONT" &&
              !(childType === "Text" && hasChildren) && (
                <FormField
                  control={childForm.control}
                  name="markup"
                  render={({ field }) => (
                    <FormItem
                      className={cn("flex flex-col space-y-2 @md:w-full", {
                        "space-y-1": compact,
                      })}
                    >
                      <FormLabel
                        className={cn("mb-0 whitespace-nowrap", {
                          "text-xs": compact,
                        })}
                      >
                        Markup %
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          value={field.value ?? ""}
                          readOnly={
                            childType === "Separator" ||
                            childType === "Signature"
                          }
                          className={cn({
                            "px-2 py-1 h-7 text-xs": compact,
                          })}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

            {!isTemplate && (
              <FormItem
                className={cn("flex flex-col space-y-2 @md:w-full", {
                  "space-y-1": compact,
                })}
              >
                <FormLabel
                  className={cn("mb-0 whitespace-nowrap", {
                    "text-xs": compact,
                  })}
                >
                  Price
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    value={
                      childType === "Text" && hasChildren
                        ? childrenTotalPrice.toFixed(2)
                        : childForm.watch("quantity") != null &&
                          childForm.watch("unitCost") != null
                        ? (
                            (childForm.watch("quantity") ?? 0) *
                            (childForm.watch("unitCost") ?? 0) *
                            (1 + (childForm.watch("markup") ?? 0) / 100)
                          ).toFixed(2)
                        : 0
                    }
                    readOnly
                    className={cn({
                      "px-2 py-1 h-7 text-xs": compact,
                    })}
                  />
                </FormControl>
              </FormItem>
            )}

            {!isTemplate && (
              <FormField
                control={childForm.control}
                name="excludeAdditionalMarkup"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      "flex flex-col-reverse items-start space-y-0",
                      {
                        "scale-90": compact,
                        hidden: !!value.parentTextTrackingId,
                      }
                    )}
                  >
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={
                          childType === "Separator" || childType === "Signature"
                        }
                      />
                    </FormControl>
                    <div
                      className={cn("space-y-2 leading-none", {
                        "space-y-1": compact,
                      })}
                    >
                      <div
                        className={cn("flex items-center gap-1 @md:mb-5", {
                          "@md:mb-3": compact,
                        })}
                      >
                        <FormLabel
                          className={cn("mb-0 whitespace-nowrap", {
                            "text-xs": compact,
                          })}
                        >
                          Excl {field.value}
                        </FormLabel>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle
                                className={cn("w-4 h-4", {
                                  "w-3 h-3": compact,
                                })}
                              />
                            </TooltipTrigger>
                            <TooltipPortal>
                              <TooltipContent>
                                <p>Exclude Additional Markup</p>
                              </TooltipContent>
                            </TooltipPortal>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={childForm.control}
              name="applyTax"
              render={({ field }) => (
                <FormItem
                  className={cn("flex flex-col-reverse items-start space-y-0", {
                    "scale-90": compact,
                    hidden: childType === "Text" && hasChildren,
                  })}
                >
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={
                        childType === "Separator" || childType === "Signature"
                      }
                    />
                  </FormControl>
                  <div
                    className={cn("space-y-2 leading-none", {
                      "space-y-1": compact,
                    })}
                  >
                    <FormLabel
                      className={cn("mb-0 @md:!mb-5 whitespace-nowrap", {
                        "text-xs @md:mb-3": compact,
                      })}
                    >
                      Tax
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

interface LineItemFormProps {
  value: GroupedLineItem;
  productStyleOptions: OptionWithGreatGrandChildren[];
  onRemove: () => void;
  onChange: (value: LineItem) => void;
  onChildrenChange?: (children: GroupedLineItem[]) => void;
  apiStage?: string;
  isTemplate?: boolean;
  id?: string;
  activeId?: string | null;
  activeItemIndex?: number;
  warranties?: Warranty[];
  materialFormulas?: MaterialFormula[];
  quoteCalculator?: QuoteCalculator | null;
  crewRates?: CrewRates | null;
  laborTypeOptions?: LaborTypeOption[];
}

export function LineItemForm({
  value,
  productStyleOptions,
  onRemove,
  onChange,
  onChildrenChange,
  apiStage,
  isTemplate,
  id: trackingId,
  activeId,
  activeItemIndex = -1,
  warranties = [],
  materialFormulas = [],
  quoteCalculator = null,
  crewRates = null,
  laborTypeOptions,
}: Readonly<LineItemFormProps>) {
  const form = useForm<LineItem>({
    resolver: zodResolver(LineItemSchema),
    defaultValues: value,
  });

  const enableFormulas = apiStage === "dev";

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: trackingId || "default" });

  // Add function to handle removing a product from text item
  const handleRemoveProductFromText = (productTrackingId: string) => {
    if (!onChildrenChange || !(value as GroupedLineItem).children) return;

    const newProducts =
      (value as GroupedLineItem).children?.filter(
        (p) => p.trackingId !== productTrackingId
      ) || [];

    onChildrenChange(newProducts);
  };
  const [parentTransform, setParentTransform] = React.useState<{
    y: number;
    index: number;
  } | null>(null);

  // Adjust transform to modify parent transform if present AND if the conditions are met
  const adjustedTransform = React.useMemo(() => {
    if (!transform || !parentTransform) return transform;

    // Only adjust if:
    // 1. There is an active drag operation (activeId exists)
    // 2. The item being dragged is not this item
    // 3. We know the index of both the group and the dragged item
    const isThisItemBeingDragged = activeId === trackingId;
    const validIndices = activeItemIndex !== -1 && parentTransform.index !== -1;

    if (activeId && !isThisItemBeingDragged && validIndices) {
      // If the group's index is higher than the dragged item's index, subtract the transform
      // If the group's index is lower than the dragged item's index, add the transform
      const isGroupAfterDraggedItem = parentTransform.index > activeItemIndex;

      return {
        ...transform,
        y: isGroupAfterDraggedItem
          ? (transform.y || 0) - (parentTransform.y || 0) // Subtract when group is after dragged item
          : (transform.y || 0) + (parentTransform.y || 0), // Add when group is before dragged item
      };
    }

    return transform;
  }, [transform, parentTransform, activeId, trackingId, activeItemIndex]);

  const style = {
    transform: CSS.Transform.toString(adjustedTransform || transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Add state for Text Product section
  const [isTextProductsCollapsed, setIsTextProductsCollapsed] =
    React.useState(false);

  // Create a ref to find parent SortableGroupHeader elements
  const elementRef = React.useRef<HTMLDivElement>(null);

  // Effect to detect if we're inside a SortableGroupHeader and find its transform
  React.useEffect(() => {
    if (!elementRef.current) return;

    // Look for parent elements with transform styles that might be SortableGroupHeaders
    let parent = elementRef.current.parentElement;
    let foundGroupHeader = false;
    let groupPosition = -1;

    while (parent && !foundGroupHeader) {
      // Check if this is a SortableGroupHeader by checking for data attributes or class names
      if (
        parent.classList.contains("group-header") ||
        parent.getAttribute("data-group-header") === "true"
      ) {
        foundGroupHeader = true;

        // Try to find the group position/index
        const groupIndexAttr = parent.getAttribute("data-group-index");

        if (groupIndexAttr) {
          groupPosition = parseInt(groupIndexAttr, 10);
        }

        const computedStyle = window.getComputedStyle(parent);
        const transform = computedStyle.transform;

        // If parent has a transform and it's not 'none', parse it to extract translateY
        if (transform && transform !== "none") {
          // This regex extracts the translateY value from a transform matrix
          const match = transform.match(/matrix\(.*,\s*(\d+\.?\d*)\)/);
          if (match && match[1]) {
            const translateY = parseFloat(match[1]);
            if (!isNaN(translateY) && translateY !== 0) {
              setParentTransform({ y: translateY, index: groupPosition });
              break;
            }
          }
        }
      }

      parent = parent.parentElement;
    }

    // If we didn't find a group header, clear any previous parent transform
    if (!foundGroupHeader) {
      setParentTransform(null);
    }
  }, []);

  const type = form.watch("type");

  const notifyChanges = useCallback(
    (data: LineItem | GroupedLineItem) => {
      if (data.parentTextTrackingId) {
        onChildrenChange?.(
          (
            value.children?.map((item) => ({
              ...item,
              ...(data.trackingId === item.trackingId ? data : {}),
            })) || []
          ).concat(
            [data as GroupedLineItem].filter(
              (newItem) =>
                !value.children?.some(
                  (item) => item.trackingId === newItem.trackingId
                )
            )
          )
        );
      } else {
        onChange(data as LineItem);
      }
    },
    [onChange, onChildrenChange, value]
  );

  return (
    <motion.div
      initial={!isDragging ? { opacity: 1, height: "auto" } : undefined}
      exit={!isDragging ? { opacity: 0, height: 0 } : undefined}
      transition={!isDragging ? { duration: 0.2 } : undefined}
      ref={elementRef}
    >
      <Card
        ref={setNodeRef}
        key={value.trackingId}
        className="overflow-hidden"
        style={{
          ...style,
        }}
      >
        {/* This is a list of all the colors for the line item types since they need to be defined statically at least once to be bundled. */}
        <div className="hidden text-purple-500 text-blue-500 text-emerald-600 text-green-500 text-amber-600 text-orange-400 bg-purple-500 bg-blue-500 bg-emerald-600 bg-green-500 bg-amber-600 bg-orange-400 bg-red-500 border-purple-500 border-blue-500 border-emerald-600 border-green-500 border-amber-600 border-orange-400 border-red-500 border-l-amber-600 border-l-primary border-slate-500 border-stone-500 bg-slate-500 bg-stone-500 text-slate-500 text-stone-500"></div>
        <CardContent
          className={cn(
            "flex flex-col border-l-4 py-6 space-y-4 @xl:w-full @xl:flex-row @xl:space-y-0 @xl:space-x-2 @xl:p-2 @xl:[&_*]:!text-xs @xl:overflow-hidden",
            `border-${LINE_ITEM_TYPES.find((t) => t.value === type)?.color}`
          )}
        >
          <div className="relative flex flex-row-reverse items-start @xl:flex-row @xl:grow-0 @xl:pr-4 flex-none">
            <div className="absolute @xl:relative top-[-0.25rem] @xl:top-4 flex items-center gap-2">
              <button
                {...attributes}
                {...listeners}
                className="cursor-move touch-none"
                type="button"
              >
                <GripVertical className="w-4 h-4" />
              </button>
              <div className="flex relative">
                {(() => {
                  const typeInfo = LINE_ITEM_TYPES.find(
                    (t) => t.value === type
                  );
                  if (!typeInfo) return null;
                  const Icon = typeInfo.icon;
                  return (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex relative gap-2 items-center p-2 rounded-md border">
                            <Icon
                              className={`w-4 h-4 text-${typeInfo.color}`}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipPortal>
                          <TooltipContent>
                            <p>{typeInfo.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {typeInfo.description}
                            </p>
                          </TooltipContent>
                        </TooltipPortal>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })()}
              </div>
              <div className="flex relative">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon-sm"
                            className="w-5 h-5"
                          >
                            <MinusIcon className="w-2.5 h-2.5" />
                            <span className="sr-only">Remove product</span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                          <div className="space-y-4">
                            <p className="text-sm">
                              Are you sure you want to remove this item?
                            </p>
                            <div className="flex gap-2 justify-end">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={(e) => {
                                  e.preventDefault();
                                  const popover =
                                    e.currentTarget.closest('[role="dialog"]');
                                  if (popover) {
                                    const trigger = document.querySelector(
                                      `[aria-controls="${popover.id}"]`
                                    );
                                    trigger?.dispatchEvent(
                                      new Event("click", { bubbles: true })
                                    );
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                variant="destructive"
                                onClick={onRemove}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent>
                        <p>Remove</p>
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>

          <div className="flex overflow-x-hidden flex-col flex-1">
            <LineItemFields
              value={value}
              productStyleOptions={productStyleOptions}
              warranties={warranties}
              materialFormulas={materialFormulas}
              quoteCalculator={quoteCalculator}
              isTemplate={isTemplate}
              notifyChanges={onChange}
              enableFormulas={enableFormulas}
              laborTypeOptions={laborTypeOptions}
              crewRates={crewRates}
            />

            {/* Text item products section */}
            {value.type === "Text" && (
              <div className="pt-4 mt-4 w-full border-t">
                <div className="flex justify-between items-center mb-2">
                  {value.children && value.children.length > 0 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setIsTextProductsCollapsed(!isTextProductsCollapsed)
                      }
                      className="flex gap-1 items-center"
                    >
                      {isTextProductsCollapsed ? (
                        <ChevronRightIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                      <span className="font-medium">Items</span>
                    </Button>
                  ) : (
                    <div></div>
                  )}

                  <div className="flex gap-2 items-center space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex gap-1 items-center"
                      onClick={() => {
                        if (!onChildrenChange) return;

                        const newItem: GroupedLineItem = {
                          trackingId: crypto.randomUUID(),
                          type: "Product",
                          title: "New Product",
                          description: null,
                          quantity: 1,
                          unit: "EA",
                          unitCost: null,
                          cost: null,
                          markup: null,
                          applyTax: true,
                          excludeAdditionalMarkup: false,
                          materialTypeId: null,
                          materialStyleId: null,
                          materialSupplierId: null,
                          laborCategoryId: null,
                          laborTypeId: null,
                          typeId: null,
                          useQuoteFile: false,
                          useClaimFile: false,
                          useWorkOrderFile: false,
                          date: null,
                          accepted: false,
                          initials: null,
                          reason: null,
                          reasonNotes: null,
                          order: null,
                          materialStyle: null,
                          groupName: null,
                          isCollapsed: false,
                          sectionId: value.sectionId,
                          appliedFormulaId: null,
                          parentTextTrackingId: value.trackingId,
                          children: [],
                        };

                        const newChildren = [
                          ...((value as GroupedLineItem).children || []),
                          newItem,
                        ];
                        onChildrenChange(newChildren);
                      }}
                    >
                      <PlusCircle className="w-3 h-3" />
                      <span>Add Product</span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex gap-1 items-center"
                      onClick={() => {
                        if (!onChildrenChange) return;

                        const newItem: GroupedLineItem = {
                          trackingId: crypto.randomUUID(),
                          type: "Labor",
                          title: "New Labor",
                          description: null,
                          quantity: 1,
                          unit: "HR",
                          unitCost: null,
                          cost: null,
                          markup: null,
                          applyTax: true,
                          excludeAdditionalMarkup: false,
                          materialTypeId: null,
                          materialStyleId: null,
                          materialSupplierId: null,
                          laborCategoryId: null,
                          laborTypeId: null,
                          typeId: null,
                          useQuoteFile: false,
                          useClaimFile: false,
                          useWorkOrderFile: false,
                          date: null,
                          accepted: false,
                          initials: null,
                          reason: null,
                          reasonNotes: null,
                          order: null,
                          materialStyle: null,
                          groupName: null,
                          isCollapsed: false,
                          sectionId: value.sectionId,
                          appliedFormulaId: null,
                          parentTextTrackingId: value.trackingId,
                          children: [],
                        };

                        const newChildren = [
                          ...((value as GroupedLineItem).children || []),
                          newItem,
                        ];
                        onChildrenChange(newChildren);
                      }}
                    >
                      <PlusCircle className="w-3 h-3" />
                      <span>Add Labor</span>
                    </Button>
                  </div>
                </div>

                {!isTextProductsCollapsed && (
                  <div className="pl-4 space-y-2">
                    {/* Products List */}
                    {(!value.children || value.children.length === 0) && (
                      <div
                        className={cn(
                          "overflow-hidden transition-all duration-300 ease-in-out",
                          activeId ? "h-auto opacity-100" : "h-0 opacity-0"
                        )}
                      >
                        <EmptyTextProductsPlaceholder
                          textId={value.trackingId}
                        />
                      </div>
                    )}

                    {value.children && value.children.length > 0 && (
                      <div className="space-y-2">
                        {value.children.map((child) => (
                          <SortableLineItem
                            key={child.trackingId}
                            item={child}
                            onRemove={handleRemoveProductFromText}
                          >
                            <LineItemFields
                              value={child}
                              compact={true}
                              productStyleOptions={productStyleOptions}
                              warranties={warranties}
                              materialFormulas={materialFormulas}
                              quoteCalculator={quoteCalculator}
                              isTemplate={isTemplate}
                              notifyChanges={notifyChanges}
                              enableFormulas={enableFormulas}
                              laborTypeOptions={laborTypeOptions}
                              crewRates={crewRates}
                            />
                          </SortableLineItem>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

LineItemForm.displayName = "LineItemForm";

// Replace the ProductDragHandle component with a complete SortableProductItem component
function SortableLineItem({
  item,
  onRemove,
  children,
}: {
  item: GroupedLineItem;
  onRemove: (itemId: string) => void;
  children: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.trackingId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-1.5 border rounded-md border-l-4 border-l-${
        LINE_ITEM_TYPES.find((type) => type.value === item.type)?.color
      }`}
    >
      <div className="flex items-center mr-1">
        <button
          {...attributes}
          {...listeners}
          className="cursor-move touch-none"
          type="button"
        >
          <GripVertical className="w-3 h-3" />
        </button>
      </div>
      <div className="flex-1">{children}</div>

      <div className="flex items-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon-sm"
                    className="w-5 h-5"
                  >
                    <MinusIcon className="w-2.5 h-2.5" />
                    <span className="sr-only">
                      Remove {item.type.toLowerCase()}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80">
                  <div className="space-y-4">
                    <p className="text-sm">
                      Are you sure you want to remove this{" "}
                      {item.type.toLowerCase()}?
                    </p>
                    <div className="flex gap-2 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          const popover =
                            e.currentTarget.closest('[role="dialog"]');
                          if (popover) {
                            const trigger = document.querySelector(
                              `[aria-controls="${popover.id}"]`
                            );
                            trigger?.dispatchEvent(
                              new Event("click", {
                                bubbles: true,
                              })
                            );
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => onRemove(item.trackingId)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent>
                <p>Remove {item.type.toLowerCase()}</p>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
